import type { ReactElement } from "react";
import { useEffect, useReducer, useState } from "react";
import { AcceleratorBatch } from "../../models/AcceleratorBatch";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import { SELECT_ALL_BATCH_INSTANCES } from "../../stores/impl/AcceleratorDocStore";
import { BatchInstanceFilter } from "../batchInstanceSelection/BatchInstanceFilter";
import { BatchInstanceSearch } from "../batchInstanceSelection/BatchInstanceSearch3";
import { Button } from "../buttons/Button";
import { CollapsiblePanel } from "../CollapsiblePanel";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTDeleteBatches,
  TTDocumentSelectorPanel,
  TTReprocessBatches,
  TTSaveTemplates,
} from "../tooltips/TTDocumentSelector";
import "../../styles/panels/batchListPanel.css";
import { DocumentStatus } from "../../types/DocumentStatuses";

export { BatchListPanel };
type Props = {
  children: (
    /**
     * Selected Batch Instances
     */
    selected: string[],
    /**
     * If all batches are selected
     */
    allSelected: boolean,
    selectBatch: (batchUuid: string) => void,
    selectAll: (
      val: undefined | boolean,
      filter: DocumentStatus | undefined,
    ) => void,
    collapsed: string[],
    allCollapsed: boolean,
    collapseBatch: (batchUuid: string) => void,
    collapseAll: (val: undefined | boolean) => void,
  ) => ReactElement | ReactElement[];
  docTypes: DocTypeConfig[];
  /**
   * Current AcceleratorBatch filter.
   */
  batchFilter: string;
  allBatches: AcceleratorBatch[];
  /**
   * AcceleratorBatches that can be selected. Includes searched batches
   */
  availableBatches: AcceleratorBatch[];
  /**
   * Visible AcceleratorBatches.
   */
  visibleBatches: AcceleratorBatch[];
  defaultSearchTerm: string;
  searchedBatches: (batchInstanceUuid: string[] | null, filter: string) => void;
  /**
   * Change visible AcceleratorBatch. SELECT_ALL_BATCH_INSTANCES can be provided.
   */
  changeBatch: (batchInstanceUuid: string) => void;
  /**
   * Reprocess Batches. SELECT_ALL_BATCH_INSTANCES can be provided.
   */
  reprocessBatches: (batchInstanceUuid: string[]) => void;
  /**
   * Delete Batches. SELECT_ALL_BATCH_INSTANCES can be provided.
   */
  deleteBatches: (batchInstanceUuid: string[]) => void;
  /**
   * Trigger save of current Model.
   */
  saveModel: () => void;
};

type Action = { selected: boolean; uuids: string[] };
function reducer(state: string[], { selected, uuids }: Action) {
  let newState;

  if (selected) {
    newState = [];
    for (let i = 0; i < uuids.length; ++i) {
      if (!state.includes(uuids[i])) {
        newState.push(uuids[i]);
      }
    }
    newState = state.concat(newState);
  } else {
    newState = state.filter((x) => !uuids.includes(x));
  }

  return newState;
}

/**
 * Component that renders Panel for BatchInstanceFilter, Reprocess/Delete/Save buttons, DocumentSelectorList.
 * @param props
 * @returns
 */
const BatchListPanel = (props: Props) => {
  // open by default
  const [open, setOpen] = useState<boolean>(true);
  /*
   * List of selected Batches
   */
  const [selected, dispatch] = useReducer(reducer, []);
  const [collapsed, dispatchCollapsed] = useReducer(reducer, []);

  // clear selection when visible batch changes
  useEffect(() => {
    dispatch({
      selected: false,
      uuids: selected,
    });
    dispatchCollapsed({
      selected: false,
      uuids: collapsed,
    });
  }, [props.batchFilter]);

  const plural =
    1 < (selected.length ? selected.length : props.visibleBatches.length);

  return (
    <CollapsiblePanel
      title={""}
      open={open}
      className={"batchListPanel"}
      disableClick
      headerChildren={
        <div
          className={"left"}
          onClick={(e) => {
            // if (e.target == e.currentTarget) {
            //     setOpen(!open);
            // }
            e.stopPropagation();
          }}
        >
          <AdvancedTooltip tooltipText={TTDocumentSelectorPanel}>
            {(tProps) => (
              <span {...tProps} className={"title"}>
                Document Selector
              </span>
            )}
          </AdvancedTooltip>
          <span className={"batchListSearching"}>
            <BatchInstanceFilter
              allOption={{
                label:
                  (props.availableBatches.length === props.allBatches.length
                    ? "All Batches"
                    : "Searched Batches") +
                  ` (${props.availableBatches.length})`,
                value: SELECT_ALL_BATCH_INSTANCES,
              }}
              availableOptions={[...(props.availableBatches ?? [])]}
              // this component is not responsible for updating whether the
              // current document is visible, the DocumentSelectorList does that.
              // yuck
              changeFilter={props.changeBatch}
              selectedFilter={props.batchFilter}
            />
            <BatchInstanceSearch
              defaultSearchTerm={props.defaultSearchTerm}
              allAcceleratorBatches={props.allBatches}
              docTypes={props.docTypes}
              onSearch={(uuids, filter) => {
                dispatch({
                  uuids: selected,
                  selected: false,
                });
                props.searchedBatches(uuids, filter);
              }}
            />
          </span>
          <span className={"batchListActions"}>
            <AdvancedTooltip tooltipText={TTSaveTemplates}>
              {(tProps) => (
                <Button
                  {...tProps}
                  className={"white"}
                  data-save-models
                  onClick={() => props.saveModel()}
                >
                  Save Template
                </Button>
              )}
            </AdvancedTooltip>
            <AdvancedTooltip tooltipText={TTReprocessBatches}>
              {(tProps) => (
                <Button
                  {...tProps}
                  className={"white"}
                  data-reprocess
                  onClick={() =>
                    props.reprocessBatches(
                      selected.length
                        ? selected
                        : props.visibleBatches.map((x) => x.uuid),
                    )
                  }
                >
                  {plural ? "Reprocess Batches" : "Reprocess Batch"}
                </Button>
              )}
            </AdvancedTooltip>
            <AdvancedTooltip tooltipText={TTDeleteBatches}>
              {(tProps) => (
                <Button
                  {...tProps}
                  className={"white"}
                  data-delete
                  onClick={() =>
                    props.deleteBatches(
                      selected.length
                        ? selected
                        : props.visibleBatches.map((x) => x.uuid),
                    )
                  }
                >
                  {plural ? "Delete Batches" : "Delete Batch"}
                </Button>
              )}
            </AdvancedTooltip>
          </span>
        </div>
      }
    >
      <>
        {open &&
          props.children(
            selected,
            selected.length === props.visibleBatches.length,
            // select specific Batch
            (uuid) => {
              dispatch({
                uuids: [uuid],
                selected: !selected.includes(uuid),
              });
            },
            // toggle all batches
            (val, filter) => {
              let batchesToSelect = props.visibleBatches;
              // add test to logic before changing
              const selectBatches =
                val != undefined
                  ? val
                  : selected.length !== props.visibleBatches.length;
              if (selectBatches && filter) {
                batchesToSelect = batchesToSelect.filter((x) =>
                  x.metadata.find((z) => z.statuses.includes(filter)),
                );
              }

              dispatch({
                uuids: batchesToSelect.map((x) => x.uuid),
                selected: selectBatches,
              });
            },
            collapsed,
            collapsed.length === props.visibleBatches.length,
            // collapse specific Batch
            (uuid) => {
              dispatchCollapsed({
                uuids: [uuid],
                selected: !collapsed.includes(uuid),
              });
            },
            // collapse specific Batch
            (val) => {
              dispatchCollapsed({
                uuids: props.visibleBatches.map((x) => x.uuid),
                selected:
                  val != undefined
                    ? val
                    : collapsed.length !== props.visibleBatches.length,
              });
            },
          )}
      </>
    </CollapsiblePanel>
  );
};
